import { Database, TreeStructure } from "@phosphor-icons/react";
import { appPaths } from "../Routes";
import { defaultScopeValue } from "../api/activity-data";
import { useEmissionsDashboard } from "../api/dashboard";
import { months } from "../constants";
import ContentContainer from "./ContentContainer";
import DateRangeFilterDropdown, {
    useDateRangeFilterDropdown,
} from "./DateRangeFilterDropdown";
import MetricCard from "./MetricCard";
import StackedBarChart from "./StackedBarChart";
import Typography from "./Typography";
import SectionTitle from "./SectionTitle";
import { EmissionsSankeyGraph } from "./SankeyChart";
import { usePaginatedEmissionsSources } from "../api/emissions-sources";
import { formatNumber } from "../utils/formatNumber";
import EmissionsSourcesFilter from "./EmissionsSourcesFilter";
import {
    ResponsiveContainer,
    BarChart,
    XAxis,
    YAxis,
    Label,
    Bar,
} from "recharts";
import { groupBy } from "../utils/groupBy";

const EmissionsDashboard = () => {
    const {
        value: dateRangeValue,
        label,
        handleChange,
        startDate,
        endDate,
    } = useDateRangeFilterDropdown();

    const { emissionsDashboardData, isLoading } = useEmissionsDashboard(
        startDate,
        endDate
    );

    const chartData =
        emissionsDashboardData?.carbonEmissionsChartDataCo2eKg.map((data) => ({
            label: months[data.monthIndex - 1],
            ...data,
        })) ?? [];

    const emissionsSourceResponse = usePaginatedEmissionsSources();

    const { emissionsSources } = emissionsSourceResponse;

    const totalFinancedEmissions = emissionsSources.reduce(
        (total, asset) => total + asset.financedEmissionsInPeriod,
        0
    );

    const averagePCAFScoreBySector = Object.entries(
        groupBy(emissionsSources, (es) => es.sectorName)
    ).map(([sectorName, assets]) => {
        return {
            sectorName,
            id: assets[0].sectorId,
            outstandingAmount: assets.reduce(
                (acc, a) => acc + a.outstandingAmount,
                0
            ),
            financedEmissions: assets.reduce(
                (acc, a) => acc + a.financedEmissionsInPeriod,
                0
            ),
        };
    });

    if (isLoading) {
        return (
            <ContentContainer className="py-10 text-center">
                Loading...
            </ContentContainer>
        );
    }

    return (
        <div>
            <ContentContainer className="py-8">
                <Typography variant="title-medium" component="h2">
                    Financed Emissions in last year
                </Typography>
                <div className="flex items-end gap-4 mt-4">
                    <Typography
                        variant="display-large"
                        component="p"
                        className="text-[4rem] leading-[4rem]"
                    >
                        {formatNumber(totalFinancedEmissions)}
                        kg
                    </Typography>
                    <Typography variant="headline-large" component="p">
                        CO<sub>2</sub>e
                    </Typography>
                </div>
                <EmissionsSourcesFilter
                    hideDataStatusFilter={false}
                    {...emissionsSourceResponse}
                />
                <div className="my-6"></div>
                <EmissionsSankeyGraph emissionsSources={emissionsSources} />
                <SectionTitle title="Sectoral Financed Emissions Breakdown" />
                <ResponsiveContainer
                    width={"100%"}
                    height={300}
                    className="my-10"
                >
                    <BarChart
                        width={150}
                        height={40}
                        data={averagePCAFScoreBySector}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <XAxis dataKey="sectorName" />
                        <YAxis
                            yAxisId="left"
                            orientation="left"
                            stroke="#8884d8"
                        >
                            <Label
                                value="Financed Emissions (kg CO2e)"
                                offset={10}
                                position="top"
                            />
                        </YAxis>
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            stroke="#82ca9d"
                        >
                            <Label
                                value="Outstanding Amount (£)"
                                position="top"
                                offset={10}
                            />
                        </YAxis>
                        <Bar
                            yAxisId="left"
                            dataKey="financedEmissions"
                            fill="#8884d8"
                        />
                        <Bar
                            yAxisId="right"
                            dataKey="outstandingAmount"
                            fill="#82ca9d"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </ContentContainer>
            <ContentContainer className="py-5">
                <DateRangeFilterDropdown
                    value={dateRangeValue}
                    onChange={handleChange}
                    selectedTextOverride={label}
                    disabledOptions={["all"]}
                />
            </ContentContainer>
            <ContentContainer>
                <Typography variant="title-medium" component="h2">
                    Carbon emissions
                </Typography>
                <div className="flex items-end gap-4 mt-4">
                    <Typography
                        variant="display-large"
                        component="p"
                        className="text-[4rem] leading-[4rem]"
                    >
                        {emissionsDashboardData?.totalCarbonEmissionsCo2eKg.toLocaleString()}
                        kg
                    </Typography>
                    <Typography variant="headline-large" component="p">
                        CO<sub>2</sub>e
                    </Typography>
                </div>
                <StackedBarChart
                    keys={[
                        {
                            name: "Scope 1",
                            colour: "var(--scope-1)",
                        },
                        {
                            name: "Scope 2",
                            colour: "var(--scope-2)",
                        },
                        {
                            name: "Scope 3",
                            colour: "var(--scope-3)",
                        },
                    ]}
                    data={chartData}
                />
                <div className="md:grid grid-cols-2 gap-6 space-y-6 md:space-y-0">
                    <MetricCard
                        title="Activity Data Points"
                        value={emissionsDashboardData?.activityDataPointsCount}
                        icon={TreeStructure}
                        href={`${appPaths.activityData}?dateRange=${dateRangeValue}&scope=${defaultScopeValue}`}
                    />
                    <MetricCard
                        title="Emissions Sources"
                        value={emissionsDashboardData?.allEmissionsSourcesCount}
                        icon={Database}
                        href={`${appPaths.emissionsSources()}`}
                    />
                </div>
            </ContentContainer>
        </div>
    );
};

export default EmissionsDashboard;
